/**
 * ``form_populate`` stores each field as a rich entry
 * ``{ name, label, type, value, confidence, source_file, ... }``.
 * The edit form expects flat ``{ fieldName: value }``. These helpers
 * bridge the two shapes.
 */

export type RichFormFieldEntry = {
  name?: string;
  label?: string;
  type?: string;
  value?: unknown;
  confidence?: number | null;
  source_file?: { doc_id?: string; label?: string } | null;
  source_reference?: string | null;
  history?: unknown[];
};

export function isRichFormFieldEntry(v: unknown): v is RichFormFieldEntry {
  return (
    typeof v === "object" &&
    v !== null &&
    !Array.isArray(v) &&
    "value" in v &&
    ("name" in v || "type" in v || "label" in v)
  );
}

/** Extract the scalar stored on a ``form_populate`` entry or pass through flat values. */
export function unwrapFormFieldValue(entry: unknown): unknown {
  if (isRichFormFieldEntry(entry)) {
    return entry.value;
  }
  if (
    typeof entry === "object" &&
    entry !== null &&
    !Array.isArray(entry) &&
    "value" in entry
  ) {
    return (entry as RichFormFieldEntry).value;
  }
  return entry;
}

/** Normalise a stored value for form controls / display (arrays → comma-separated text). */
export function formFieldEditValue(value: unknown): unknown {
  const unwrapped = unwrapFormFieldValue(value);
  if (unwrapped === undefined || unwrapped === null) return unwrapped;
  if (
    typeof unwrapped === "string" ||
    typeof unwrapped === "number" ||
    typeof unwrapped === "boolean"
  ) {
    return unwrapped;
  }
  if (Array.isArray(unwrapped)) {
    return unwrapped
      .map((part) => String(part).trim())
      .filter(Boolean)
      .join(", ");
  }
  if (typeof unwrapped === "object") {
    return String(unwrapped);
  }
  return unwrapped;
}

/** Flat values for ``DocumentFormView`` controls. */
export function formDataToEditValues(
  formData: Record<string, unknown>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, raw] of Object.entries(formData)) {
    if (key.startsWith("_")) continue;
    out[key] = formFieldEditValue(raw);
  }
  return out;
}

/** Merge flat edits back into the stored rich ``form_data`` blob. */
export function mergeFormEditValues(
  existing: Record<string, unknown>,
  edits: Record<string, unknown>,
): Record<string, unknown> {
  const out: Record<string, unknown> = { ...existing };
  for (const [key, editValue] of Object.entries(edits)) {
    if (key.startsWith("_")) continue;
    const prev = existing[key];
    if (
      isRichFormFieldEntry(prev) ||
      (typeof prev === "object" &&
        prev !== null &&
        !Array.isArray(prev) &&
        "value" in prev)
    ) {
      out[key] = { ...(prev as RichFormFieldEntry), value: editValue };
    } else {
      out[key] = editValue;
    }
  }
  return out;
}

export function formatFormDisplayValue(value: unknown): string {
  const normalized = formFieldEditValue(value);
  if (normalized === undefined || normalized === null) return "—";
  if (typeof normalized === "boolean") return normalized ? "Yes" : "No";
  if (typeof normalized === "number") return String(normalized);
  const s = String(normalized).trim();
  return s.length > 0 ? s : "—";
}

/** True when TipTap body is empty (data-only ``form`` artefacts). */
export function isEmptyLetterContent(
  content: Record<string, unknown> | undefined,
): boolean {
  if (!content) return true;
  const blocks = content.content;
  if (!Array.isArray(blocks) || blocks.length === 0) return true;
  return blocks.every((block) => {
    if (!block || typeof block !== "object") return true;
    const b = block as Record<string, unknown>;
    if (b.type === "paragraph") {
      const inner = b.content;
      if (!Array.isArray(inner) || inner.length === 0) return true;
      return inner.every((node) => {
        if (!node || typeof node !== "object") return true;
        const n = node as Record<string, unknown>;
        if (n.type === "hardBreak") return true;
        const text = n.text;
        return typeof text !== "string" || text.trim() === "";
      });
    }
    return false;
  });
}
